import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { apiUrl, getErrorMessage, mediaUrl } from '../utils/api';
import { HALF_HOUR_TIME_OPTIONS } from '../utils/appointmentTimeOptions';

const defaultBusinessHours = { monday: { open: '09:00', close: '17:00', closed: false }, tuesday: { open: '09:00', close: '17:00', closed: false }, wednesday: { open: '09:00', close: '17:00', closed: false }, thursday: { open: '09:00', close: '17:00', closed: false }, friday: { open: '09:00', close: '17:00', closed: false }, saturday: { open: '09:00', close: '15:00', closed: false }, sunday: { open: '09:00', close: '15:00', closed: true } };
const businessDayLabels = [['monday','Monday'],['tuesday','Tuesday'],['wednesday','Wednesday'],['thursday','Thursday'],['friday','Friday'],['saturday','Saturday'],['sunday','Sunday']];
const hydrateBusinessHours = (savedHours = {}) => Object.fromEntries(
  businessDayLabels.map(([key]) => [key, { ...defaultBusinessHours[key], ...(savedHours?.[key] || {}) }]),
);
const emptyBusiness = { name: '', slug: '', type: 'salon', description: '', phone: '', email: '', address: '', city: '', country: 'Belize', timezone: 'America/Belize', latitude: '', longitude: '', active: true, heroImage: '', heroImageData: '', cancellationPolicy: '', businessHours: defaultBusinessHours };
const emptyService = { name: '', category: 'Beauty', description: '', durationMinutes: 60, price: 0, active: true };
const emptyStaff = { userId: '', displayName: '', title: 'Beauty Professional', bio: '', imageUrl: '', imageData: '' };
const emptyInvite = { email: '', role: 'staff' };

const appointmentActionsByStatus = {
  requested: [
    { status: 'confirmed', label: 'Confirm' },
    { status: 'reschedule_requested', label: 'Request reschedule' },
    { status: 'cancelled', label: 'Cancel', danger: true },
  ],
  pending: [
    { status: 'confirmed', label: 'Confirm' },
    { status: 'reschedule_requested', label: 'Request reschedule' },
    { status: 'cancelled', label: 'Cancel', danger: true },
  ],
  confirmed: [
    { status: 'completed', label: 'Mark completed' },
    { status: 'reschedule_requested', label: 'Request reschedule' },
    { status: 'no_show', label: 'Mark no-show' },
    { status: 'cancelled', label: 'Cancel', danger: true },
  ],
  reschedule_requested: [
    { status: 'confirmed', label: 'Confirm new time' },
    { status: 'cancelled', label: 'Cancel', danger: true },
  ],
};

const appointmentGuidance = {
  requested: 'Awaiting owner or manager confirmation.',
  pending: 'Awaiting owner or manager confirmation.',
  confirmed: 'Confirmed — update this after the appointment or if plans change.',
  reschedule_requested: 'A new time is being requested.',
  completed: 'This appointment is complete.',
  cancelled: 'This appointment was cancelled.',
  no_show: 'This appointment was marked as a no-show.',
};


const appointmentServiceIdsKey = (appointment) => {
  const items = (appointment?.serviceItems || []).map((item) => item?.serviceRef?._id || item?.serviceRef).filter(Boolean).map(String);
  const ids = items.length ? items : [appointment?.serviceRef?._id || appointment?.serviceRef].filter(Boolean).map(String);
  return [...new Set(ids)].join(',');
};

function ReschedulePicker({ appointment, businessId, authConfig, onSave, onCancel, busy }) {
  const initialDate = appointment.date ? new Date(appointment.date).toISOString().slice(0, 10) : '';
  const serviceIdsKey = appointmentServiceIdsKey(appointment);
  const staffProfileId = appointment.staffProfile?._id || appointment.staffProfile || '';
  const [date, setDate] = useState(initialDate);
  const [slots, setSlots] = useState([]);
  const [totalDurationMinutes, setTotalDurationMinutes] = useState(0);
  const [time, setTime] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!date || !serviceIdsKey) { setSlots([]); setTotalDurationMinutes(0); return; }
    let cancelled = false;
    setLoading(true);
    const staffQuery = staffProfileId ? `&staffProfileId=${encodeURIComponent(staffProfileId)}` : '';
    axios.get(apiUrl(`/api/bookings/available?businessId=${encodeURIComponent(businessId)}&serviceIds=${encodeURIComponent(serviceIdsKey)}${staffQuery}&date=${encodeURIComponent(date)}&excludeAppointmentId=${encodeURIComponent(appointment._id)}`))
      .then(({ data }) => {
        if (cancelled) return;
        const next = data.slots || [];
        setTotalDurationMinutes(data.totalDurationMinutes || 0);
        setSlots(next);
        setTime((current) => next.some((slot) => slot.time === current && slot.available) ? current : (next.find((slot) => slot.available)?.time || ''));
      })
      .catch(() => { if (!cancelled) { setSlots([]); setTotalDurationMinutes(0); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [appointment._id, businessId, date, serviceIdsKey, staffProfileId]);

  const availabilityCounts = useMemo(() => slots.reduce((acc, slot) => {
    if (slot.available) acc.available += 1;
    else {
      acc.unavailable += 1;
      if (slot.reason === 'Already booked' || slot.reason === 'Overlaps another appointment') acc.booked += 1;
      else if (slot.reason === 'Outside business hours' || slot.reason === 'Not enough time before closing') acc.outsideHours += 1;
      else if (slot.reason === 'Closed') acc.closed += 1;
    }
    return acc;
  }, { available: 0, unavailable: 0, booked: 0, outsideHours: 0, closed: 0 }), [slots]);

  return (
    <div className="reschedule-picker">
      <strong>Choose a new date and available time</strong>
      <p className="muted-text">Unavailable times are shown clearly and cannot be selected.</p>
      <div className="form-grid">
        <label>Date<input type="date" value={date} min={new Date().toISOString().slice(0, 10)} onChange={(e) => { setDate(e.target.value); setTime(''); }} /></label>
      </div>
      {date && !loading && (
        <div className="availability-panel">
          <div className="availability-summary"><strong>{date}</strong><span>{totalDurationMinutes} min total · {availabilityCounts.available} available · {availabilityCounts.unavailable} unavailable</span></div>
          <div className="availability-legend" aria-label="Availability legend">
            <span className="availability-pill availability-pill-available">Available {availabilityCounts.available}</span>
            <span className="availability-pill availability-pill-booked">Booked {availabilityCounts.booked}</span>
            <span className="availability-pill availability-pill-hours">Outside hours {availabilityCounts.outsideHours}</span>
            <span className="availability-pill availability-pill-closed">Closed {availabilityCounts.closed}</span>
          </div>
          {slots.length === 0 ? <p className="muted-text">No time slots are available on this date. Please choose another day.</p> : (
            <div className="time-grid">
              {slots.map((slot) => (
                <button key={slot.time} type="button" className={slot.available ? (time === slot.time ? 'time-chip active' : 'time-chip') : 'time-chip unavailable'} disabled={!slot.available} aria-disabled={!slot.available} onClick={() => setTime(slot.time)} title={slot.reason || ''}>
                  <span>{slot.time}</span>
                  {!slot.available && <small>{slot.reason}</small>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {loading && <p className="muted-text">Checking availability…</p>}
      <div className="owner-actions">
        <button type="button" disabled={busy || !date || !time} onClick={() => onSave({ status: 'confirmed', date, time })}>Save new date &amp; time</button>
        <button type="button" className="ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

const appointmentDateLabel = (value) => {
  if (!value) return 'the selected date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'the selected date';
  return `${String(date.getUTCDate()).padStart(2, '0')}/${String(date.getUTCMonth() + 1).padStart(2, '0')}/${date.getUTCFullYear()}`;
};

function AssignmentPicker({ appointment, businessId, staff, authConfig, busy, onSave, onCancel }) {
  const serviceIdsKey = appointmentServiceIdsKey(appointment);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    const appointmentDate = appointment.date ? new Date(appointment.date) : null;
    const date = appointmentDate ? appointmentDate.toISOString().slice(0, 10) : '';
    const people = (staff || []).filter((person) => person.active !== false);
    Promise.all(people.map(async (person) => {
      try {
        const { data } = await axios.get(apiUrl(`/api/bookings/available?businessId=${encodeURIComponent(businessId)}&serviceIds=${encodeURIComponent(serviceIdsKey)}&staffProfileId=${encodeURIComponent(person._id)}&date=${encodeURIComponent(date)}&excludeAppointmentId=${encodeURIComponent(appointment._id)}`));
        const slot = (data.slots || []).find((item) => item.time === appointment.time);
        return { person, available: Boolean(slot?.available), reason: slot?.reason || (slot ? 'Unavailable' : 'No matching time') };
      } catch (err) { return { person, available: false, reason: 'Could not check availability' }; }
    })).then((next) => { if (!cancelled) setOptions(next); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [appointment._id, appointment.date, appointment.time, appointment.serviceItems, appointment.serviceRef, serviceIdsKey, businessId, staff]);
  return <div className="assignment-picker panel"><div className="assignment-picker-head"><strong>Assign professional</strong><button type="button" className="ghost" onClick={onCancel}>Close</button></div><p className="muted-text">Availability for {appointmentDateLabel(appointment.date)} at {appointment.time}.</p>{loading ? <p className="muted-text">Checking availability…</p> : <div className="assignment-options">{options.map((option) => <button type="button" key={option.person._id} disabled={!option.available || busy} className={`assignment-option ${option.available ? 'available' : 'unavailable'}`} onClick={() => option.available && onSave({ status: appointment.status, staffProfileId: option.person._id })}><strong>{option.person.displayName}</strong><span>{option.available ? 'Available' : option.reason}</span></button>)}{!options.length && <p className="muted-text">No active professionals are available to check.</p>}</div>}</div>;
}

function OwnerDashboard() {
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [serviceImageBusy, setServiceImageBusy] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [businessForm, setBusinessForm] = useState(emptyBusiness);
  const [businessHeroImageData, setBusinessHeroImageData] = useState('');
  const [serviceForm, setServiceForm] = useState(emptyService);
  const [serviceEditForm, setServiceEditForm] = useState(emptyService);
  const [editingServiceId, setEditingServiceId] = useState('');
  const [staffForm, setStaffForm] = useState(emptyStaff);
  const [staffEditForm, setStaffEditForm] = useState(emptyStaff);
  const [editingStaffId, setEditingStaffId] = useState('');
  const [inviteForm, setInviteForm] = useState(emptyInvite);
  const [inviteLink, setInviteLink] = useState('');
  const [createMode, setCreateMode] = useState(false);
  const [appointmentDateFilter, setAppointmentDateFilter] = useState('');
  const [appointmentStatusFilter, setAppointmentStatusFilter] = useState('all');
  const [appointmentQuickFilter, setAppointmentQuickFilter] = useState('upcoming');
  const [showAllAppointments, setShowAllAppointments] = useState(false);
  const [appointmentSearch, setAppointmentSearch] = useState('');
  const [rescheduleAppointmentId, setRescheduleAppointmentId] = useState('');
  const [assignmentAppointmentId, setAssignmentAppointmentId] = useState('');
  const [manualAppointmentForm, setManualAppointmentForm] = useState({ customerName: '', customerEmail: '', customerPhone: '', serviceIds: [], staffProfileId: '', date: '', time: '', notes: '' });
  const [quickActionsOpen, setQuickActionsOpen] = useState(true);
  const [teamToolsOpen, setTeamToolsOpen] = useState(false);
  const [reviewResponseDrafts, setReviewResponseDrafts] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [whatsappUsage, setWhatsappUsage] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [analyticsFrom, setAnalyticsFrom] = useState('');
  const [analyticsTo, setAnalyticsTo] = useState('');
  const createBusinessRef = useRef(null);
  const navigate = useNavigate();
  const userInfo = useMemo(() => JSON.parse(localStorage.getItem('userInfo') || 'null'), []);
  const authConfig = useMemo(() => (userInfo?.token ? { headers: { Authorization: 'Bearer ' + userInfo.token } } : null), [userInfo?.token]);
  const isAdmin = userInfo?.role === 'admin';
  const selectedMembershipRole = userInfo?.businessRoles?.find((membership) => String(membership.business) === String(selectedBusinessId))?.role;
  const canManageBusiness = isAdmin || ['owner', 'manager'].includes(selectedMembershipRole);
  const canEditCatalog = canManageBusiness;
  const canManageAppointments = canManageBusiness;
  const canCreateManualAppointment = isAdmin || ['owner', 'manager'].includes(selectedMembershipRole);
  const canManageMembers = isAdmin || selectedMembershipRole === 'owner';
  const selectedManualServices = useMemo(() => {
    const servicesById = new Map((dashboard?.services || []).map((service) => [String(service._id), service]));
    return (manualAppointmentForm.serviceIds || []).map((id) => servicesById.get(String(id))).filter(Boolean);
  }, [dashboard?.services, manualAppointmentForm.serviceIds]);
  const manualAppointmentTotalDuration = selectedManualServices.reduce((sum, service) => sum + Number(service.durationMinutes || 0), 0);
  const manualAppointmentTotalPrice = selectedManualServices.reduce((sum, service) => sum + Number(service.price || 0), 0);

  const filteredAppointments = useMemo(() => {
    const now = new Date();
    const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const toLocalKey = (dateValue) => {
      const apptDate = dateValue ? new Date(dateValue) : null;
      if (!apptDate || Number.isNaN(apptDate.getTime())) return '';
      return `${apptDate.getFullYear()}-${String(apptDate.getMonth() + 1).padStart(2, '0')}-${String(apptDate.getDate()).padStart(2, '0')}`;
    };
    return (dashboard?.appointments || []).filter((appointment) => {
      const search = appointmentSearch.trim().toLowerCase();
      if (search) {
        const haystack = [
          appointment.service,
          appointment.user?.name,
          appointment.user?.email,
          appointment.staffProfile?.displayName,
          appointment.staffName,
          appointment.status,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      if (appointmentStatusFilter !== 'all' && appointment.status !== appointmentStatusFilter) return false;
      const appointmentKey = toLocalKey(appointment?.date);
      if (appointmentDateFilter && appointmentKey !== appointmentDateFilter) return false;
      if (appointmentQuickFilter !== 'all') {
        if (!appointmentKey) return false;
        const dayDiff = Math.round((new Date(`${appointmentKey}T00:00:00`).getTime() - new Date(`${todayKey}T00:00:00`).getTime()) / 86400000);
        if (appointmentQuickFilter === 'today' && dayDiff !== 0) return false;
        if (appointmentQuickFilter === 'upcoming' && dayDiff < 0) return false;
        if (appointmentQuickFilter === 'past' && dayDiff >= 0) return false;
      }
      return true;
    });
  }, [dashboard?.appointments, appointmentDateFilter, appointmentStatusFilter, appointmentQuickFilter, appointmentSearch]);

  const visibleAppointments = showAllAppointments ? filteredAppointments : filteredAppointments.slice(0, 10);

  const appointmentStatusCounts = useMemo(() => {
    return (dashboard?.appointments || []).reduce((acc, appointment) => {
      acc[appointment.status] = (acc[appointment.status] || 0) + 1;
      return acc;
    }, { requested: 0, pending: 0, confirmed: 0, reschedule_requested: 0, completed: 0, cancelled: 0, no_show: 0 });
  }, [dashboard?.appointments]);

  const scrollToSection = (sectionId, attempt = 0) => {
    if (typeof document === 'undefined') return;
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (attempt < 20) window.setTimeout(() => scrollToSection(sectionId, attempt + 1), 150);
  };
  const pendingAppointmentTarget = typeof window !== 'undefined' && window.location.hash === '#owner-appointments';

  const loadBusinesses = useCallback(async () => {
    try {
      const { data } = await axios.get(apiUrl('/api/owner/businesses'), authConfig);
      setBusinesses(data);
      if (data.length && !selectedBusinessId && !createMode) setSelectedBusinessId(data[0]._id);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not load owner businesses.'));
    }
  }, [authConfig, selectedBusinessId, createMode]);

  const loadDashboard = useCallback(async (businessId) => {
    try {
      const { data } = await axios.get(apiUrl(`/api/owner/businesses/${businessId}/dashboard`), authConfig);
      setDashboard(data);
      setBusinessHeroImageData('');
      if (businessId) { const usageResponse = await axios.get(apiUrl(`/api/owner/businesses/${businessId}/whatsapp-usage`), authConfig); setWhatsappUsage(usageResponse.data); }
      setBusinessForm({
        name: data.business?.name || '',
        slug: data.business?.slug || '',
        type: data.business?.type || 'salon',
        description: data.business?.description || '',
        phone: data.business?.phone || '',
        email: data.business?.email || '',
        address: data.business?.address || '',
        city: data.business?.city || '',
        country: data.business?.country || 'Belize',
        timezone: data.business?.timezone || 'America/Belize',
        active: data.business?.active !== false,
        heroImage: data.business?.heroImage || '',
        cancellationPolicy: data.business?.cancellationPolicy || '',
        businessHours: hydrateBusinessHours(data.business?.businessHours),
        latitude: data.business?.latitude ?? '',
        longitude: data.business?.longitude ?? '',
      });
      setServiceForm(emptyService);
      setServiceEditForm(emptyService);
      setEditingServiceId('');
      setStaffForm(emptyStaff);
      setStaffEditForm(emptyStaff);
      setEditingStaffId('');
      setInviteForm(emptyInvite);
      setInviteLink('');
      setCreateMode(false);
      setError('');
    } catch (err) {
      setError(getErrorMessage(err, 'Could not load business dashboard.'));
    }
  }, [authConfig]);

  const loadAnalytics = useCallback(async (businessId, days = analyticsDays) => {
    if (!businessId || !authConfig || (days === 'custom' && (!analyticsFrom || !analyticsTo || analyticsTo < analyticsFrom))) return;
    try {
      const query = days === 'custom' ? `from=${encodeURIComponent(analyticsFrom)}&to=${encodeURIComponent(analyticsTo)}&v=2` : `days=${days}&v=2`;
      const { data } = await axios.get(apiUrl(`/api/owner/businesses/${businessId}/analytics?${query}`), authConfig);
      setAnalytics(data?.access === 'locked' ? data : { ...data, summary: data?.summary || {}, trend: Array.isArray(data?.trend) ? data.trend : [], topServices: Array.isArray(data?.topServices) ? data.topServices : [], topProfessionals: Array.isArray(data?.topProfessionals) ? data.topProfessionals : [], advanced: data?.advanced ? { ...data.advanced, revenueByService: Array.isArray(data.advanced.revenueByService) ? data.advanced.revenueByService : [], revenueByProfessional: Array.isArray(data.advanced.revenueByProfessional) ? data.advanced.revenueByProfessional : [], busiestDays: Array.isArray(data.advanced.busiestDays) ? data.advanced.busiestDays : [], busiestTimes: Array.isArray(data.advanced.busiestTimes) ? data.advanced.busiestTimes : [], topCustomers: Array.isArray(data.advanced.topCustomers) ? data.advanced.topCustomers : [] } : null });
    } catch (err) {
      setAnalytics(null);
      setError(getErrorMessage(err, 'Could not load business performance insights.'));
    }
  }, [analyticsDays, analyticsFrom, analyticsTo, authConfig]);

  useEffect(() => {
    if (!userInfo?.token) { navigate('/login'); return; }
    loadBusinesses();
  }, [navigate, userInfo?.token, loadBusinesses]);

  useEffect(() => { if (selectedBusinessId) { loadDashboard(selectedBusinessId); loadAnalytics(selectedBusinessId, analyticsDays); } else setAnalytics(null); }, [selectedBusinessId, loadDashboard, loadAnalytics, analyticsDays, analyticsFrom, analyticsTo]);

  useEffect(() => {
    if (!pendingAppointmentTarget) return;
    const retry = window.setInterval(() => {
      const section = document.getElementById('owner-appointments');
      if (section) { window.clearInterval(retry); scrollToSection('owner-appointments'); }
    }, 300);
    window.setTimeout(() => window.clearInterval(retry), 8000);
    return () => window.clearInterval(retry);
  }, [dashboard, pendingAppointmentTarget]);

  useEffect(() => { if (!authConfig) return; axios.get(apiUrl('/api/owner/invoices'), authConfig).then(({ data }) => setInvoices(data || [])).catch(() => {}); }, [authConfig, selectedBusinessId]);

  useEffect(() => {
    if (createMode) {
      createBusinessRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [createMode]);

  const submitBusiness = async (event) => {
    event.preventDefault();
    if (!selectedBusinessId) return;
    if (!canEditCatalog) { setError('Only owners and managers can update business settings.'); return; }
    setBusy(true); setMessage(''); setError('');
    try {
      await axios.put(apiUrl(`/api/owner/businesses/${selectedBusinessId}`), { ...businessForm, heroImageData: businessHeroImageData || undefined }, authConfig);
      setMessage('Business settings saved.');
      await loadBusinesses();
      await loadDashboard(selectedBusinessId);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save business settings.'));
    } finally { setBusy(false); }
  };


  const saveCancellationPolicy = async () => {
    if (!selectedBusinessId || !canManageBusiness) return;
    setBusy(true); setMessage(''); setError('');
    try {
      const { data } = await axios.put(apiUrl(`/api/owner/businesses/${selectedBusinessId}`), { cancellationPolicy: businessForm.cancellationPolicy || '' }, authConfig);
      setBusinessForm((current) => ({ ...current, cancellationPolicy: data.cancellationPolicy || '' }));
      setMessage('Cancellation policy saved.');
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the cancellation policy.'));
    } finally { setBusy(false); }
  };

  const submitCreateBusiness = async (event) => {
    event.preventDefault();
    setBusy(true); setMessage(''); setError('');
    try {
      const { data } = await axios.post(apiUrl('/api/owner/businesses'), { ...businessForm, heroImageData: businessHeroImageData || undefined }, authConfig);
      setMessage(`Business created: ${data.name}`);
      if (businessHeroImageData && !data?.heroImage) setError('The business was created, but its hero image was not saved. Upload it again from Business settings.');
      setCreateMode(false);
      await loadBusinesses();
      if (data._id) {
        setSelectedBusinessId(data._id);
        await loadDashboard(data._id);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Could not create business.'));
    } finally { setBusy(false); }
  };

  const submitService = async (event) => {
    event.preventDefault();
    if (!selectedBusinessId) return;
    if (!canEditCatalog) { setError('Only owners and managers can manage services.'); return; }
    setBusy(true); setMessage(''); setError('');
    try {
      await axios.post(apiUrl(`/api/owner/businesses/${selectedBusinessId}/services`), serviceForm, authConfig);
      setMessage('Service created.');
      setServiceForm(emptyService);
      await loadDashboard(selectedBusinessId);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not create service.'));
    } finally { setBusy(false); }
  };

  const readImageAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const uploadBusinessHeroImage = async (event) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    const extension = String(file.name || '').split('.').pop().toLowerCase();
    const mimeByExtension = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp' };
    const mimeType = file.type || mimeByExtension[extension] || '';
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(mimeType)) {
      setError('Please choose a JPEG, PNG, or WebP image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Hero images must be 5 MB or smaller.');
      return;
    }

    setBusy(true); setMessage(''); setError('');
    try {
      const rawData = await readImageAsDataUrl(file);
      const imageData = String(rawData || '').replace(/^data:[^;]*;base64,/, `data:${mimeType};base64,`);
      if (!imageData.startsWith(`data:${mimeType};base64,`)) throw new Error('The image could not be read. Please choose a JPEG, PNG, or WebP image.');
      setBusinessHeroImageData(imageData);
      setBusinessForm((current) => ({ ...current, heroImageData: imageData }));
      if (createMode) {
        setMessage('Hero image selected. It will be saved when you create the business.');
        return;
      }
      if (!selectedBusinessId) throw new Error('Select a business before uploading its hero image.');
      const { data } = await axios.put(apiUrl(`/api/owner/businesses/${selectedBusinessId}`), { heroImageData: imageData }, authConfig);
      if (!data?.heroImage) throw new Error('The server did not confirm that the hero image was saved. Please retry.');
      setBusinessForm((current) => ({ ...current, heroImage: data.heroImage, heroImageData: '' }));
      setBusinessHeroImageData('');
      setMessage('Hero image uploaded and saved.');
      await loadBusinesses();
      await loadDashboard(selectedBusinessId);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not upload the hero image. Please try again.'));
    } finally { setBusy(false); }
  };

  const uploadServiceImages = async (service, event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    if (!canEditCatalog) { setError('Only owners and managers can manage service images.'); return; }
    if ((service.images?.length || 0) + files.length > 8) { setError('Each service can have up to 8 images.'); return; }
    const invalid = files.find((file) => !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024);
    if (invalid) { setError('Use JPEG, PNG, or WebP images up to 5 MB each.'); return; }
    setServiceImageBusy(service._id); setMessage(''); setError('');
    try {
      for (const file of files) {
        const imageData = await readImageAsDataUrl(file);
        await axios.post(apiUrl(`/api/owner/services/${service._id}/images`), { imageData }, authConfig);
      }
      setMessage(`${files.length} service image${files.length === 1 ? '' : 's'} uploaded.`);
      await loadDashboard(selectedBusinessId);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not upload service images.'));
    } finally { setServiceImageBusy(''); }
  };

  const deleteServiceImage = async (service, imageId) => {
    if (!canEditCatalog) return;
    if (!window.confirm('Remove this service image?')) return;
    setServiceImageBusy(service._id); setMessage(''); setError('');
    try {
      await axios.delete(apiUrl(`/api/owner/services/${service._id}/images/${imageId}`), authConfig);
      setMessage('Service image removed.');
      await loadDashboard(selectedBusinessId);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not remove the service image.'));
    } finally { setServiceImageBusy(''); }
  };

  const startEditService = (service) => {
    setEditingServiceId(service._id);
    setServiceEditForm({
      name: service.name || '',
      category: service.category || 'Beauty',
      description: service.description || '',
      durationMinutes: service.durationMinutes || 60,
      price: service.price || 0,
      active: service.active !== false,
    });
  };

  const submitServiceEdit = async (event) => {
    event.preventDefault();
    if (!editingServiceId) return;
    if (!canEditCatalog) { setError('Only owners and managers can manage services.'); return; }
    setBusy(true); setMessage(''); setError('');
    try {
      await axios.put(apiUrl(`/api/owner/services/${editingServiceId}`), serviceEditForm, authConfig);
      setMessage('Service updated.');
      setEditingServiceId('');
      setServiceEditForm(emptyService);
      await loadDashboard(selectedBusinessId);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not update service.'));
    } finally { setBusy(false); }
  };

  const deleteService = async (serviceId) => {
    if (!canEditCatalog) { setError('Only owners and managers can manage services.'); return; }
    if (!window.confirm('Delete this service?')) return;
    setBusy(true); setMessage(''); setError('');
    try {
      await axios.delete(apiUrl(`/api/owner/services/${serviceId}`), authConfig);
      setMessage('Service deleted.');
      await loadDashboard(selectedBusinessId);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not delete service.'));
    } finally { setBusy(false); }
  };

  const submitStaff = async (event) => {
    event.preventDefault();
    if (!selectedBusinessId || editingStaffId) return;
    if (!canEditCatalog) { setError('Only owners and managers can manage staff profiles.'); return; }
    setBusy(true); setMessage(''); setError('');
    try {
      await axios.post(apiUrl(`/api/owner/businesses/${selectedBusinessId}/staff`), staffForm, authConfig);
      setMessage('Staff profile created.');
      setStaffForm(emptyStaff);
      await loadDashboard(selectedBusinessId);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not create staff profile.'));
    } finally { setBusy(false); }
  };

  const startEditStaff = (person) => {
    setEditingStaffId(person._id);
    setStaffEditForm({
      displayName: person.displayName || '',
      title: person.title || '',
      bio: person.bio || '',
      imageUrl: person.imageUrl || '',
      imageData: '',
      active: person.active !== false,
      bufferMinutes: person.bufferMinutes ?? 0,
      services: Array.isArray(person.services) ? person.services.map((service) => service._id || service) : [],
      user: person.user?._id || person.user || '',
    });
    setStaffForm(emptyStaff);
    setMessage('');
    setError('');
  };

  const cancelEditStaff = () => {
    setEditingStaffId('');
    setStaffEditForm(emptyStaff);
  };

  const submitStaffEdit = async (event) => {
    event.preventDefault();
    if (!editingStaffId) return;
    if (!canEditCatalog) { setError('Only owners and managers can manage staff profiles.'); return; }
    setBusy(true); setMessage(''); setError('');
    try {
      await axios.put(apiUrl(`/api/owner/staff/${editingStaffId}`), staffEditForm, authConfig);
      setMessage('Staff profile updated.');
      cancelEditStaff();
      await loadDashboard(selectedBusinessId);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not update staff profile.'));
    } finally { setBusy(false); }
  };

  const deleteStaff = async (staffId) => {
    if (!canEditCatalog) { setError('Only owners and managers can manage staff profiles.'); return; }
    if (!window.confirm('Delete this staff profile?')) return;
    setBusy(true); setMessage(''); setError('');
    try {
      await axios.delete(apiUrl(`/api/owner/staff/${staffId}`), authConfig);
      setMessage('Staff profile deleted.');
      if (editingStaffId === staffId) cancelEditStaff();
      await loadDashboard(selectedBusinessId);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not delete staff profile.'));
    } finally { setBusy(false); }
  };

  const submitInvite = async (event) => {
    event.preventDefault();
    if (!selectedBusinessId) return;
    if (!canManageMembers) { setError('Only owners can create team invitations.'); return; }
    setBusy(true); setMessage(''); setError(''); setInviteLink('');
    try {
      const { data } = await axios.post(apiUrl(`/api/owner/businesses/${selectedBusinessId}/invites`), inviteForm, authConfig);
      setMessage(`Invite created for ${data.email}`);
      setInviteForm(emptyInvite);
      await loadDashboard(selectedBusinessId);
      setInviteLink(data.inviteUrl);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not create invite.'));
    } finally { setBusy(false); }
  };

  const copyInviteLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setMessage("Invite link copied.");
      setError("");
    } catch (err) {
      setError("Could not copy automatically. Select the displayed invite URL and copy it manually.");
    }
  };

  const updateMember = async (memberId, payload) => {
    if (!canManageMembers) { setError('Only owners can change team permissions.'); return; }
    setBusy(true); setMessage(''); setError('');
    try {
      await axios.patch(apiUrl(`/api/owner/members/${memberId}`), payload, authConfig);
      setMessage('Member updated.');
      await loadDashboard(selectedBusinessId);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not update member.'));
    } finally { setBusy(false); }
  };

  const deleteMember = async (memberId) => {
    if (!canManageMembers) { setError('Only owners can remove team members.'); return; }
    if (!window.confirm('Remove this member from the business?')) return;
    setBusy(true); setMessage(''); setError('');
    try {
      await axios.delete(apiUrl(`/api/owner/members/${memberId}`), authConfig);
      setMessage('Member removed.');
      await loadDashboard(selectedBusinessId);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not remove member.'));
    } finally { setBusy(false); }
  };

  const respondToReview = async (reviewId) => {
    const body = String(reviewResponseDrafts[reviewId] || '').trim();
    if (!body) { setError('Write a response before sending it.'); return; }
    setBusy(true); setMessage(''); setError('');
    try { await axios.post(apiUrl(`/api/owner/reviews/${reviewId}/response`), { body }, authConfig); setMessage('Review response saved.'); await loadDashboard(selectedBusinessId); }
    catch (err) { setError(getErrorMessage(err, 'Could not save review response.')); }
    finally { setBusy(false); }
  };

  const reportReview = async (reviewId) => {
    const reason = window.prompt('Why should this review be reviewed by platform administrators?');
    if (!reason) return;
    setBusy(true); setMessage(''); setError('');
    try { await axios.post(apiUrl(`/api/owner/reviews/${reviewId}/report`), { reason }, authConfig); setMessage('Review reported to platform administrators.'); await loadDashboard(selectedBusinessId); }
    catch (err) { setError(getErrorMessage(err, 'Could not report review.')); }
    finally { setBusy(false); }
  };


  const submitManualAppointment = async (event) => {
    event.preventDefault();
    if (!selectedBusinessId || !canCreateManualAppointment) { setError('Only an owner or manager can create appointments for this business.'); return; }
    if (!(manualAppointmentForm.serviceIds || []).length) { setError('Please choose at least one service.'); return; }
    setBusy(true); setMessage(''); setError('');
    try {
      await axios.post(apiUrl(`/api/owner/businesses/${selectedBusinessId}/appointments`), manualAppointmentForm, authConfig);
      setMessage('Appointment created and confirmed.');
      setManualAppointmentForm({ customerName: '', customerEmail: '', customerPhone: '', serviceIds: [], staffProfileId: '', date: '', time: '', notes: '' });
      await loadDashboard(selectedBusinessId);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not create appointment.'));
    } finally { setBusy(false); }
  };

  const requestWhatsAppCredits = async () => {
    const credits = Number(window.prompt('How many WhatsApp credits would you like to request?', '500'));
    if (!Number.isInteger(credits) || credits < 1) return;
    const reference = window.prompt('Payment reference (optional):', '') || '';
    try { await axios.post(apiUrl(`/api/owner/businesses/${selectedBusinessId}/whatsapp-credits/request`), { credits, paymentReference: reference }, authConfig); setMessage('WhatsApp credit request submitted for admin review.'); }
    catch (err) { setError(getErrorMessage(err, 'Could not request WhatsApp credits.')); }
  };

  const updateAppointment = async (appointmentId, payload) => {
    if (!canManageAppointments) { setError('Only owners and managers can update appointments.'); return; }
    setBusy(true); setMessage(''); setError('');
    try {
      await axios.patch(apiUrl(`/api/owner/appointments/${appointmentId}`), payload, authConfig);
      setMessage('Appointment updated.');
      await loadDashboard(selectedBusinessId);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not update appointment.'));
    } finally { setBusy(false); }
  };

  if (!userInfo?.token) return null;

  return (
    <div className="page owner-page">
      <section className="split-page">
        <div>
          <p className="eyebrow">Owner dashboard</p>
          <h1>Manage businesses, services, staff, invites, and appointments.</h1>
          <p className="muted-text">Invite staff by email, assign manager or staff access, and control each location separately.</p>
        </div>
        <div className="panel">
          <h2>{userInfo.name}</h2>
          <p className="muted-text">Platform role: {userInfo.role}</p>
          <p className="muted-text">Business role: {selectedBusinessId ? (selectedMembershipRole || (isAdmin ? 'admin override' : 'viewer')) : 'select a business'}</p>
          <p className="muted-text">Accessible businesses: {businesses.length}</p>
        </div>
      </section>

      {message && <p className="success-message">{message}</p>}
      {error && <p className="error-message">{error}</p>}

      <section className="owner-toolbar panel">
        <label>
          Select business
          <select value={selectedBusinessId} onChange={(e) => { setSelectedBusinessId(e.target.value); setAnalytics(null); setShowAllAppointments(false); setCreateMode(false); }}>
            <option value="">Choose a business</option>
            {businesses.map((business) => <option key={business._id} value={business._id}>{business.name} — {business.city}</option>)}
          </select>
        </label>
        {isAdmin && <button type="button" className="ghost owner-new-business-btn" onClick={() => { setMessage('Create mode opened.'); setCreateMode(true); setSelectedBusinessId(''); setDashboard(null); setBusinessForm(emptyBusiness); }}>New Business</button>}
      </section>

      <nav className="owner-section-nav panel" aria-label="Owner dashboard sections"><span className="owner-section-nav-label">Jump to</span><button type="button" onClick={() => scrollToSection('owner-analytics')}>Insights</button><button type="button" onClick={() => scrollToSection('owner-appointments')}>Appointments</button><button type="button" onClick={() => scrollToSection('owner-invoices')}>Invoices</button><button type="button" onClick={() => scrollToSection('owner-reviews')}>Reviews</button><button type="button" onClick={() => scrollToSection('owner-whatsapp')}>WhatsApp</button><button type="button" onClick={() => scrollToSection('owner-services')}>Services</button><button type="button" onClick={() => scrollToSection('owner-staff')}>Professionals</button></nav>

      <section className="owner-layout">
        <form className="panel owner-form" onSubmit={createMode ? submitCreateBusiness : submitBusiness}>
          <p className="eyebrow">Business settings</p>
          <h2 className="owner-form-title">{createMode ? 'Create business' : 'Edit business'}</h2>
          {!createMode && !canEditCatalog && <p className="muted-text permission-note">Staff access is view-only here. Owners and managers can edit business settings, services, staff profiles, and appointments.</p>}
          <div className="owner-grid-two">
            <input disabled={!createMode && !canEditCatalog} value={businessForm.name} onChange={(e) => setBusinessForm({ ...businessForm, name: e.target.value })} placeholder="Business name" required />
            <input disabled={!createMode && !canEditCatalog} value={businessForm.slug} onChange={(e) => setBusinessForm({ ...businessForm, slug: e.target.value })} placeholder="Slug" />
            <select disabled={!createMode && !canEditCatalog} value={businessForm.type} onChange={(e) => setBusinessForm({ ...businessForm, type: e.target.value })}>
              <option value="salon">Salon</option><option value="barber">Barber</option><option value="spa">Spa</option><option value="nails">Nails</option><option value="lashes">Lashes</option><option value="massage">Massage</option><option value="wellness">Wellness</option><option value="other">Other</option>
            </select>
            <input disabled={!createMode && !canEditCatalog} value={businessForm.city} onChange={(e) => setBusinessForm({ ...businessForm, city: e.target.value })} placeholder="City" />
            <input disabled={!createMode && !canEditCatalog} value={businessForm.email} onChange={(e) => setBusinessForm({ ...businessForm, email: e.target.value })} placeholder="Email" />
            <input disabled={!createMode && !canEditCatalog} value={businessForm.phone} onChange={(e) => setBusinessForm({ ...businessForm, phone: e.target.value })} placeholder="Phone" />
            <input disabled={!createMode && !canEditCatalog} value={businessForm.address} onChange={(e) => setBusinessForm({ ...businessForm, address: e.target.value })} placeholder="Address" />
            <input disabled={!createMode && !canEditCatalog} type="number" min="-90" max="90" step="any" value={businessForm.latitude} onChange={(e) => setBusinessForm({ ...businessForm, latitude: e.target.value })} placeholder="Latitude (optional)" />
            <input disabled={!createMode && !canEditCatalog} type="number" min="-180" max="180" step="any" value={businessForm.longitude} onChange={(e) => setBusinessForm({ ...businessForm, longitude: e.target.value })} placeholder="Longitude (optional)" />
            <input disabled={!createMode && !canEditCatalog} value={businessForm.heroImage} onChange={(e) => setBusinessForm({ ...businessForm, heroImage: e.target.value })} placeholder="Hero image URL (optional)" />
            <label className="file-upload-field">Hero image upload<input disabled={(!createMode && !canEditCatalog) || busy} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadBusinessHeroImage} /><small className="muted-text">JPEG, PNG, or WebP up to 5 MB. For a new business, the image is saved when you create it.</small></label>
            {businessHeroImageData || businessForm.heroImage ? <img className="business-hero-upload-preview" src={businessHeroImageData || mediaUrl(businessForm.heroImage)} alt="Business hero preview" /> : null}
            <select disabled={!createMode && !canEditCatalog} value={String(businessForm.active)} onChange={(e) => setBusinessForm({ ...businessForm, active: e.target.value === 'true' })}>
              <option value="true">active</option>
              <option value="false">inactive</option>
            </select>
          </div>
          <textarea disabled={!createMode && !canEditCatalog} rows="4" value={businessForm.description} onChange={(e) => setBusinessForm({ ...businessForm, description: e.target.value })} placeholder="Description" />
          <label className="cancellation-policy-editor"><span className="eyebrow">Customer policy</span><strong>Cancellation policy</strong><textarea disabled={!createMode && !canEditCatalog} rows="5" maxLength="3000" value={businessForm.cancellationPolicy || ''} onChange={(e) => setBusinessForm({ ...businessForm, cancellationPolicy: e.target.value })} placeholder="Explain your cancellation notice period, fees, refunds, or exceptions. Leave blank if you do not have a separate policy." /><small className="muted-text">Shown on your public profile and in the booking flow. Up to 3,000 characters.</small><button type="button" className="button ghost" disabled={busy || createMode || !canManageBusiness} onClick={saveCancellationPolicy}>Save cancellation policy</button></label>
          <section className="business-hours-editor"><p className="eyebrow">Opening hours</p><h3>When customers can book</h3>{businessDayLabels.map(([key,label]) => { const day = businessForm.businessHours?.[key] || defaultBusinessHours[key]; return <div className="business-hours-editor-row" key={key}><strong>{label}</strong><label className="hours-closed"><input type="checkbox" checked={Boolean(day.closed)} disabled={!createMode && !canEditCatalog} onChange={(e) => setBusinessForm({ ...businessForm, businessHours: { ...businessForm.businessHours, [key]: { ...day, closed: e.target.checked } } })} /> Closed</label><input type="time" value={day.open} disabled={day.closed || (!createMode && !canEditCatalog)} onChange={(e) => setBusinessForm({ ...businessForm, businessHours: { ...businessForm.businessHours, [key]: { ...day, open: e.target.value } } })} /><span>to</span><input type="time" value={day.close} disabled={day.closed || (!createMode && !canEditCatalog)} onChange={(e) => setBusinessForm({ ...businessForm, businessHours: { ...businessForm.businessHours, [key]: { ...day, close: e.target.value } } })} /></div>; })}</section>
          <div className="owner-actions">
            <button type="submit" disabled={busy || (!createMode && !canEditCatalog)}>{createMode ? 'Create Business' : 'Save Business Settings'}</button>
            {createMode && <button type="button" className="ghost" onClick={() => setCreateMode(false)}>Cancel</button>}
          </div>
        </form>

        <div className="owner-main">
          {dashboard ? (
            <>
              <section className="service-grid owner-stats">
                <article className="service-card"><h3>Services</h3><strong>{dashboard.stats.services}</strong></article>
                <article className="service-card"><h3>Staff</h3><strong>{dashboard.stats.staff}</strong></article>
                <article className="service-card"><h3>Appointments</h3><strong>{dashboard.stats.appointments}</strong></article>
                <article className="service-card"><h3>Members</h3><strong>{dashboard.stats.activeMembers}</strong></article>
              </section>

              {analytics && <section id="owner-analytics" className="panel owner-analytics-panel">
                {analytics.access === 'locked' ? <div className="analytics-locked"><p className="eyebrow">Starter plan</p><h2>Business performance insights</h2><p className="muted-text">Detailed booking analytics are available on the Standard and Professional plans. Upgrade to understand booking trends, service demand, and team performance.</p><Link className="button primary" to="/pricing">View plans</Link></div> : <>
                <div className="owner-section-heading"><div><p className="eyebrow">Business performance insights</p><h2>Know what is driving your bookings</h2><p className="muted-text">A practical view of booking activity, service demand, team performance, and completed-booking value.</p></div><div className="analytics-range"><label>Period<select value={analyticsDays} onChange={(e) => setAnalyticsDays(e.target.value === 'custom' ? 'custom' : Number(e.target.value))}><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option><option value="365">Last 12 months</option><option value="custom">Custom range</option></select></label>{analyticsDays === 'custom' && <div className="analytics-custom-range"><label>From<input type="date" value={analyticsFrom} onChange={(e) => setAnalyticsFrom(e.target.value)} /></label><label>To<input type="date" value={analyticsTo} min={analyticsFrom || undefined} onChange={(e) => setAnalyticsTo(e.target.value)} /></label></div>}</div></div>
                <div className="analytics-summary-grid">
                  <article className="analytics-stat"><span>Total bookings</span><strong>{analytics.summary.totalBookings}</strong><small>excluding cancelled</small></article>
                  <article className="analytics-stat"><span>Completed</span><strong>{analytics.summary.completed}</strong><small>{analytics.summary.completionRate}% completion rate</small></article>
                  <article className="analytics-stat"><span>Awaiting confirmation</span><strong>{analytics.summary.awaitingConfirmation}</strong><small>requests needing attention</small></article>
                  <article className="analytics-stat"><span>Estimated value</span><strong>${Number(analytics.summary.estimatedRevenue || 0).toFixed(2)}</strong><small>completed services</small></article>
                </div>
                <div className="analytics-detail-grid">
                  <div><h3>Booking activity</h3><div className="analytics-trend">{(analytics.trend || []).filter((day) => day.bookings || day.completed).slice(-14).map((day) => <div className="analytics-day" key={day.date}><div className="analytics-bar" style={{ '--bar-height': `${Math.max(8, Math.min(100, day.bookings * 18))}%` }} title={`${day.bookings} bookings on ${day.date}`}><span style={{ height: `${Math.max(8, Math.min(100, day.bookings * 18))}%` }} /></div><small>{day.date.slice(5)}</small></div>)}{!(analytics.trend || []).some((day) => day.bookings) && <p className="muted-text">No booking activity in this period yet.</p>}</div><div className="analytics-status-line"><span>Cancelled {analytics.summary.cancellationRate}%</span><span>No-show {analytics.summary.noShow}</span></div></div>
                  <div><h3>Top services</h3><div className="analytics-list">{(analytics.topServices || []).length ? (analytics.topServices || []).map((item) => <div className="analytics-list-row" key={item.name}><span><strong>{item.name}</strong><small>{item.completed} completed</small></span><b>{item.bookings}</b></div>) : <p className="muted-text">No service bookings in this period.</p>}</div></div>
                  <div><h3>Professional performance</h3><div className="analytics-list">{(analytics.topProfessionals || []).length ? (analytics.topProfessionals || []).map((item) => <div className="analytics-list-row" key={item.name}><span><strong>{item.name}</strong><small>{item.completed} completed</small></span><b>{item.bookings}</b></div>) : <p className="muted-text">No professional bookings in this period.</p>}</div></div>
                </div>
              
                {analytics.advancedAvailable && analytics.advanced && <section className="advanced-analytics"><div className="owner-section-heading"><div><p className="eyebrow">Professional reporting</p><h2>Deeper reporting and insights</h2><p className="muted-text">See customer repeat activity, revenue mix, and the patterns behind your strongest booking periods.</p></div></div><div className="analytics-summary-grid advanced-summary-grid"><article className="analytics-stat"><span>Average completed booking</span><strong>${Number(analytics.advanced.averageCompletedBookingValue || 0).toFixed(2)}</strong><small>per completed service</small></article><article className="analytics-stat"><span>Unique customers</span><strong>{analytics.advanced.uniqueCustomers}</strong><small>{analytics.advanced.repeatCustomerRate}% returning customers</small></article><article className="analytics-stat"><span>Repeat customers</span><strong>{analytics.advanced.repeatCustomers}</strong><small>2 or more completed bookings</small></article></div><div className="advanced-report-grid"><div><h3>Revenue by service</h3><div className="analytics-list">{(analytics.advanced.revenueByService || []).length ? (analytics.advanced.revenueByService || []).map((item) => <div className="analytics-list-row" key={item.name}><span><strong>{item.name}</strong><small>{item.bookings} completed</small></span><b>${Number(item.revenue || 0).toFixed(2)}</b></div>) : <p className="muted-text">No completed services in this period.</p>}</div></div><div><h3>Revenue by professional</h3><div className="analytics-list">{(analytics.advanced.revenueByProfessional || []).length ? (analytics.advanced.revenueByProfessional || []).map((item) => <div className="analytics-list-row" key={item.name}><span><strong>{item.name}</strong><small>{item.bookings} completed</small></span><b>${Number(item.revenue || 0).toFixed(2)}</b></div>) : <p className="muted-text">No completed professional bookings in this period.</p>}</div></div><div><h3>Busiest days</h3><div className="analytics-list">{(analytics.advanced.busiestDays || []).slice(0,5).map((item) => <div className="analytics-list-row" key={item.day}><span><strong>{item.day}</strong></span><b>{item.bookings}</b></div>)}</div></div><div><h3>Popular booking times</h3><div className="analytics-list">{(analytics.advanced.busiestTimes || []).length ? (analytics.advanced.busiestTimes || []).map((item) => <div className="analytics-list-row" key={item.time}><span><strong>{item.time}</strong></span><b>{item.bookings}</b></div>) : <p className="muted-text">No time pattern yet.</p>}</div></div><div><h3>Top five customers</h3><div className="analytics-list">{(analytics.advanced.topCustomers || []).length ? (analytics.advanced.topCustomers || []).map((item, index) => <div className="analytics-list-row" key={`${item.email || item.name}-${index}`}><span><strong>{item.name}</strong><small>{item.completed} completed</small></span><b>{item.bookings}</b></div>) : <p className="muted-text">No customer bookings in this period.</p>}</div></div></div></section>}
                </>}
              </section>}

              <section className="owner-work-grid">
                <form id="owner-services" className="panel owner-form" onSubmit={editingServiceId ? submitServiceEdit : submitService}>
                  <p className="eyebrow">Services</p>
                  <h2 className="owner-form-title">{editingServiceId ? 'Edit service' : 'Add service'}</h2>
                  <label>
                    Service name
                    <input value={editingServiceId ? serviceEditForm.name : serviceForm.name} onChange={(e) => editingServiceId ? setServiceEditForm({ ...serviceEditForm, name: e.target.value }) : setServiceForm({ ...serviceForm, name: e.target.value })} required />
                  </label>
                  <label>
                    Category
                    <input value={editingServiceId ? serviceEditForm.category : serviceForm.category} onChange={(e) => editingServiceId ? setServiceEditForm({ ...serviceEditForm, category: e.target.value }) : setServiceForm({ ...serviceForm, category: e.target.value })} />
                  </label>
                  <div className="owner-grid-two">
                    <label>
                      Duration (minutes)
                      <input type="number" min="5" value={editingServiceId ? serviceEditForm.durationMinutes : serviceForm.durationMinutes} onChange={(e) => editingServiceId ? setServiceEditForm({ ...serviceEditForm, durationMinutes: Number(e.target.value) }) : setServiceForm({ ...serviceForm, durationMinutes: Number(e.target.value) })} />
                    </label>
                    <label>
                      Price
                      <input type="number" min="0" step="0.01" value={editingServiceId ? serviceEditForm.price : serviceForm.price} onChange={(e) => editingServiceId ? setServiceEditForm({ ...serviceEditForm, price: Number(e.target.value) }) : setServiceForm({ ...serviceForm, price: Number(e.target.value) })} />
                    </label>
                  </div>
                  <label>
                    Description
                    <textarea rows="3" value={editingServiceId ? serviceEditForm.description : serviceForm.description} onChange={(e) => editingServiceId ? setServiceEditForm({ ...serviceEditForm, description: e.target.value }) : setServiceForm({ ...serviceForm, description: e.target.value })} />
                  </label>
                  {editingServiceId ? (
                    <div className="owner-actions">
                      <label>
                        Status
                        <select value={String(serviceEditForm.active)} onChange={(e) => setServiceEditForm({ ...serviceEditForm, active: e.target.value === 'true' })}>
                          <option value="true">active</option>
                          <option value="false">inactive</option>
                        </select>
                      </label>
                      <button type="submit" disabled={busy || !canEditCatalog}>Save Service</button>
                      <button type="button" className="ghost" onClick={() => { setEditingServiceId(''); setServiceEditForm(emptyService); }}>Cancel</button>
                    </div>
                  ) : <button type="submit" disabled={busy || !canEditCatalog}>Add Service</button>}
                  <div className="owner-list">
                    {dashboard.services.map((service) => (
                      <div key={service._id} className="owner-list-item owner-member-row service-owner-row">
                        <div><strong>{service.name}</strong><div>{service.category} · {service.durationMinutes} min · ${service.price} · {service.active ? 'active' : 'inactive'}</div>
                          <div className="service-image-manager">
                            {(service.images || []).map((image) => <div className="service-image-thumb" key={image._id}><img src={mediaUrl(image.url)} alt={image.caption || `${service.name} example`} /><button type="button" className="service-image-remove" title="Remove image" disabled={serviceImageBusy === service._id || !canEditCatalog} onClick={() => deleteServiceImage(service, image._id)}>×</button></div>)}
                            <label className="service-upload-button">+ Add photos<input type="file" accept="image/jpeg,image/png,image/webp" multiple disabled={serviceImageBusy === service._id || !canEditCatalog} onChange={(event) => uploadServiceImages(service, event)} /></label>
                          </div>
                          <small className="muted-text">{service.images?.length || 0}/8 gallery images · first image is the cover</small>
                        </div>
                        <div className="owner-actions"><button type="button" className="ghost" disabled={!canEditCatalog} onClick={() => startEditService(service)}>Edit</button><button type="button" className="ghost" disabled={!canEditCatalog} onClick={() => deleteService(service._id)}>Delete</button></div>
                      </div>
                    ))}
                  </div>
                </form>

                <form id="owner-staff" className="panel owner-form" onSubmit={editingStaffId ? submitStaffEdit : submitStaff}>
                  <p className="eyebrow owner-form-kicker">Staff</p>
                  <h2 className="owner-form-title owner-form-title-center">{editingStaffId ? 'Edit professional' : 'Professional'}</h2>
                  <label>
                    Display name
                    <input value={editingStaffId ? staffEditForm.displayName : staffForm.displayName} onChange={(e) => editingStaffId ? setStaffEditForm({ ...staffEditForm, displayName: e.target.value }) : setStaffForm({ ...staffForm, displayName: e.target.value })} required />
                  </label>
                  <label>
                    Title
                    <input value={editingStaffId ? staffEditForm.title : staffForm.title} onChange={(e) => editingStaffId ? setStaffEditForm({ ...staffEditForm, title: e.target.value }) : setStaffForm({ ...staffForm, title: e.target.value })} />
                  </label>
                  <label>
                    Professional photo
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => { const file = e.target.files?.[0]; if (!file) return; if (file.size > 2 * 1024 * 1024) { setError('Professional images must be 2 MB or smaller.'); return; } const reader = new FileReader(); reader.onload = () => editingStaffId ? setStaffEditForm({ ...staffEditForm, imageData: reader.result }) : setStaffForm({ ...staffForm, imageData: reader.result }); reader.readAsDataURL(file); }} />
                    <small className="muted-text">Upload a JPEG, PNG, or WebP image up to 2 MB.</small>
                    {(editingStaffId ? (staffEditForm.imageData || staffEditForm.imageUrl) : staffForm.imageData) && <img className="staff-upload-preview" src={editingStaffId && !staffEditForm.imageData ? mediaUrl(staffEditForm.imageUrl) : (editingStaffId ? staffEditForm.imageData : staffForm.imageData)} alt="Professional preview" />}
                  </label>
                  <label>
                    About this professional
                    <textarea rows="3" value={editingStaffId ? staffEditForm.bio : staffForm.bio} onChange={(e) => editingStaffId ? setStaffEditForm({ ...staffEditForm, bio: e.target.value }) : setStaffForm({ ...staffForm, bio: e.target.value })} />
                  </label>
                  <label>Linked team login
                    <select value={editingStaffId ? (staffEditForm.user || '') : (staffForm.userId || '')} onChange={e => editingStaffId ? setStaffEditForm({ ...staffEditForm, user: e.target.value || null }) : setStaffForm({ ...staffForm, userId: e.target.value })}>
                      <option value="">Not linked</option>
                      {(dashboard.members || []).filter(m => m.active && m.user).map(m => <option key={m._id} value={m.user._id}>{m.user.name} — {m.user.email}</option>)}
                    </select>
                    <small className="muted-text">Invite the professional as Staff and have them accept first. Linking their login enables their private schedule and appointment alerts.</small>
                  </label>
                  {editingStaffId ? (
                    <div className="owner-actions">
                      <label>
                        Status
                        <select value={String(staffEditForm.active)} onChange={(e) => setStaffEditForm({ ...staffEditForm, active: e.target.value === 'true' })}>
                          <option value="true">active</option>
                          <option value="false">disabled</option>
                        </select>
                      </label>
                      <button type="submit" disabled={busy || !canEditCatalog}>Save Staff</button>
                      <button type="button" className="ghost" onClick={cancelEditStaff}>Cancel</button>
                    </div>
                  ) : <button type="submit" disabled={busy || !canEditCatalog}>Add Staff</button>}
                  <div className="owner-list">{dashboard.staff.map((person) => (
                    <div key={person._id} className="owner-list-item owner-member-row">
                      <div>
                        <div className="owner-professional-row"><div className="professional-photo professional-photo-small">{person.imageUrl ? <img src={mediaUrl(person.imageUrl)} alt="" /> : <span>{String(person.displayName || "P").slice(0, 1).toUpperCase()}</span>}</div><strong>{person.displayName}</strong></div>
                        <div>{person.title}{person.active === false ? ' · disabled' : ''}</div>
                      </div>
                      <div className="owner-actions">
                        <button type="button" className="ghost" disabled={!canEditCatalog} onClick={() => startEditStaff(person)}>Edit</button>
                        <button type="button" className="ghost" disabled={!canEditCatalog} onClick={() => deleteStaff(person._id)}>Remove</button>
                      </div>
                    </div>
                  ))}</div>
                </form>
              </section>

              {whatsappUsage && <section id="owner-whatsapp" className="panel owner-whatsapp-panel"><p className="eyebrow">WhatsApp messaging</p><h2>Message credits</h2><p className="muted-text">Included allowance resets monthly. Additional messages use prepaid credits at BZD $0.05 per delivered message.</p><div className="admin-stats-grid"><article className="panel admin-stat"><p className="eyebrow">This month</p><h2>{whatsappUsage.usage?.used || 0} / {whatsappUsage.allowance || 0}</h2><p className="muted-text">Used included messages</p></article><article className="panel admin-stat"><p className="eyebrow">Purchased balance</p><h2>{whatsappUsage.creditBalance || 0}</h2><p className="muted-text">Credits remaining</p></article></div><button type="button" className="button primary" onClick={requestWhatsAppCredits}>Request message credits</button></section>}
              <section id="owner-appointments" className="panel">
                <p className="eyebrow">Appointments</p>
                <h2>Manage bookings</h2>
                <div className="status-legend">
                  <span className="badge badge-requested">🟠 Requested</span>
                  <span className="badge badge-pending">🔵 Pending</span>
                  <span className="badge badge-confirmed">🟢 Confirmed</span>
                  <span className="badge badge-reschedule_requested">🟣 Reschedule</span>
                  <span className="badge badge-completed">✅ Completed</span>
                  <span className="badge badge-cancelled">⚪ Cancelled</span>
                  <span className="badge badge-no_show">⚫ No show</span>
                </div>
                {isAdmin && <p className="muted-text">Admins have override access only. Use the admin panel for platform-level intervention; owners and managers confirm appointments here.</p>}
                                {canCreateManualAppointment && <form className="owner-form owner-whatsapp-panel manual-appointment-form" onSubmit={submitManualAppointment}>
                  <p className="eyebrow">Owner booking</p>
                  <h2>Set up an appointment for a customer</h2>
                  <p className="muted-text">This appointment is created for the selected business only and is confirmed immediately. Owner and manager bookings are recorded for accountability.</p>
                  <div className="form-grid">
                    <input required placeholder="Customer name" value={manualAppointmentForm.customerName} onChange={(e) => setManualAppointmentForm({ ...manualAppointmentForm, customerName: e.target.value })} />
                    <input required type="email" placeholder="Customer email" value={manualAppointmentForm.customerEmail} onChange={(e) => setManualAppointmentForm({ ...manualAppointmentForm, customerEmail: e.target.value })} />
                    <input required type="tel" placeholder="Customer phone" value={manualAppointmentForm.customerPhone} onChange={(e) => setManualAppointmentForm({ ...manualAppointmentForm, customerPhone: e.target.value })} />
                    <fieldset className="manual-service-selection">
                      <legend>Choose service(s)</legend>
                      <div className="manual-service-options">
                        {(dashboard.services || []).filter((service) => service.active !== false).map((service) => (
                          <label className="manual-service-option" key={service._id}>
                            <input
                              type="checkbox"
                              checked={(manualAppointmentForm.serviceIds || []).includes(String(service._id))}
                              onChange={(event) => {
                                const checked = event.target.checked;
                                setManualAppointmentForm((current) => {
                                  const currentIds = current.serviceIds || [];
                                  const nextIds = checked
                                    ? [...new Set([...currentIds, String(service._id)])]
                                    : currentIds.filter((id) => String(id) !== String(service._id));
                                  return { ...current, serviceIds: nextIds };
                                });
                              }}
                            />
                            <span className="manual-service-option-copy"><strong>{service.name}</strong><small>{Number(service.durationMinutes || 0)} min · ${Number(service.price || 0).toFixed(2)}</small></span>
                          </label>
                        ))}
                      </div>
                      <p className="manual-service-summary">{selectedManualServices.length ? `${selectedManualServices.length} service${selectedManualServices.length === 1 ? '' : 's'} selected · ${manualAppointmentTotalDuration} min · $${manualAppointmentTotalPrice.toFixed(2)}` : 'Choose one or more services to book together.'}</p>
                    </fieldset>
                    <select value={manualAppointmentForm.staffProfileId} onChange={(e) => setManualAppointmentForm({ ...manualAppointmentForm, staffProfileId: e.target.value })}><option value="">Any available professional</option>{(dashboard.staff || []).filter((person) => person.active !== false).map((person) => <option key={person._id} value={person._id}>{person.displayName}</option>)}</select>
                    <label>Date<input required type="date" value={manualAppointmentForm.date} onChange={(e) => setManualAppointmentForm({ ...manualAppointmentForm, date: e.target.value })} /></label>
                    <label>Appointment time (AM/PM)<select required value={manualAppointmentForm.time} onChange={(e) => setManualAppointmentForm({ ...manualAppointmentForm, time: e.target.value })}><option value="">Select a time</option>{HALF_HOUR_TIME_OPTIONS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}</select></label>
                  </div>
                  <textarea rows="3" placeholder="Notes (optional)" value={manualAppointmentForm.notes} onChange={(e) => setManualAppointmentForm({ ...manualAppointmentForm, notes: e.target.value })} />
                  <button type="submit" disabled={busy || !canCreateManualAppointment || !(manualAppointmentForm.serviceIds || []).length}>Create confirmed appointment</button>
                </form>}
                {!canCreateManualAppointment && !isAdmin && <p className="permission-note">Managers can create appointments for this business, but cannot manage another business or change business ownership.</p>}
<div className="owner-appointment-filters">
                  <label>
                    Search
                    <input type="text" placeholder="Customer, service, staff, or status" value={appointmentSearch} onChange={(e) => setAppointmentSearch(e.target.value)} />
                  </label>
                  <label>
                    Quick filter
                    <select value={appointmentQuickFilter} onChange={(e) => setAppointmentQuickFilter(e.target.value)}>
                      <option value="all">All appointments</option>
                      <option value="today">Today</option>
                      <option value="upcoming">Upcoming</option>
                      <option value="past">Past</option>
                    </select>
                  </label>
                  <label>
                    Filter by date
                    <input type="date" value={appointmentDateFilter} onChange={(e) => setAppointmentDateFilter(e.target.value)} />
                  </label>
                  <label>
                    Status
                    <select value={appointmentStatusFilter} onChange={(e) => setAppointmentStatusFilter(e.target.value)}>
                      <option value="all">All statuses</option>
                      {['requested', 'confirmed', 'reschedule_requested', 'cancelled', 'completed', 'no_show'].map((status) => <option key={status} value={status}>{status}</option>)}
                    </select>
                  </label>
                  <button type="button" className="ghost" onClick={() => { setAppointmentSearch(''); setAppointmentQuickFilter('all'); setAppointmentDateFilter(''); setAppointmentStatusFilter('all'); }}>Clear filters</button>
                </div>
                <p className="section-note">Showing {visibleAppointments.length} of {filteredAppointments.length} matching appointments{appointmentQuickFilter === 'upcoming' ? ' · upcoming by default' : ''}.</p>
                <div className="owner-appointments">
                  {filteredAppointments.length === 0 ? <div className="owner-list-item">No appointments match the current filters.</div> : visibleAppointments.map((appointment) => (
                    <div className="owner-appointment appointment-card" key={appointment._id}>
                      <div className="appointment-card-head">
                        <div>
                          <strong>{appointment.user?.name || 'Customer'}</strong>
                           <p>{appointment.service}{appointment.user?.email ? ` · ${appointment.user.email}` : ''}</p>
                        </div>
                        {isAdmin ? <span className="badge badge-admin">Override only</span> : <span className={`badge badge-${appointment.status}`}>{appointment.status.replaceAll('_', ' ')}</span>}
                      </div>
                      <div className="appointment-card-grid">
                        <div className="appointment-card-item"><span className="appointment-card-label">Date</span><strong>{appointmentDateLabel(appointment.date)}</strong></div>
                        <div className="appointment-card-item"><span className="appointment-card-label">Time</span><strong>{appointment.time}</strong></div>
                        <div className="appointment-card-item"><span className="appointment-card-label">Staff</span><strong>{appointment.staffProfile?.displayName || appointment.staffName || 'Any available professional'}</strong></div>
                        <div className="appointment-card-item"><span className="appointment-card-label">Reference</span><strong>{appointment._id.slice(-6).toUpperCase()}</strong></div>
                      </div>
                      {appointment.notes ? <p className="appointment-notes">{appointment.notes}</p> : null}
                      {!appointment.staffProfile && !['completed', 'cancelled', 'no_show'].includes(appointment.status) && <div className="assignment-action"><button type="button" className="ghost" disabled={busy || !canManageAppointments} onClick={() => setAssignmentAppointmentId(assignmentAppointmentId === appointment._id ? '' : appointment._id)}>Assign professional</button>{assignmentAppointmentId === appointment._id && <AssignmentPicker appointment={appointment} businessId={selectedBusinessId} staff={dashboard.staff} authConfig={authConfig} busy={busy} onCancel={() => setAssignmentAppointmentId('')} onSave={async (payload) => { await updateAppointment(appointment._id, payload); setAssignmentAppointmentId(''); }} />}</div>}
                      <div className="appointment-workflow">
                        <p className="appointment-status-note">{appointmentGuidance[appointment.status] || 'Review this appointment status.'}</p>
                        {isAdmin ? (
                          <p className="appointment-admin-note">Admin override only — use the admin panel for platform-level changes.</p>
                        ) : (
                          <div className="appointment-actions">
                            {(appointmentActionsByStatus[appointment.status] || []).map((action) => (
                              <button key={action.status} type="button" className={action.danger ? 'ghost danger-action' : 'ghost'} disabled={busy || !canManageAppointments} onClick={() => action.status === 'reschedule_requested' ? setRescheduleAppointmentId(appointment._id) : updateAppointment(appointment._id, { status: action.status })}>{action.label}</button>
                            ))}
                            {!appointmentActionsByStatus[appointment.status]?.length && <span className="appointment-final-note">No further actions</span>}
                          </div>
                        )}
                        {rescheduleAppointmentId === appointment._id && <ReschedulePicker appointment={appointment} businessId={selectedBusinessId} authConfig={authConfig} busy={busy} onCancel={() => setRescheduleAppointmentId('')} onSave={async (payload) => { await updateAppointment(appointment._id, payload); setRescheduleAppointmentId(''); }} />}
                      </div>
                    </div>
                  ))}
                </div>
                {filteredAppointments.length > 10 && <button type="button" className="ghost appointments-more-button" onClick={() => setShowAllAppointments((open) => !open)}>{showAllAppointments ? 'Show fewer appointments' : `Show all ${filteredAppointments.length} appointments`}</button>}
              </section>

              <section id="owner-invoices" className="panel"><p className="eyebrow">Billing</p><h2>Invoices</h2><p className="muted-text">Your first month is free. Invoices will show here when paid billing begins.</p>{invoices.length === 0 ? <div className="owner-list-item">No invoices yet.</div> : invoices.map((invoice) => <div className="owner-list-item" key={invoice._id}><strong>{invoice.invoiceNumber}</strong><span>{invoice.business?.name || 'Business'} · ${Number(invoice.total || 0).toFixed(2)} · {invoice.status}</span><a className="button ghost" href={apiUrl(`/api/owner/invoices/${invoice._id}/pdf`)} target="_blank" rel="noreferrer">Download PDF</a></div>)}</section>

          <section id="owner-reviews" className="panel">
                <p className="eyebrow">Customer feedback</p>
                <h2>Reviews</h2>
                <p className="muted-text">Respond professionally to verified customer feedback. Report inappropriate reviews to platform administrators.</p>
                <div className="owner-appointments">
                  {(dashboard.reviews || []).length === 0 ? <div className="owner-list-item">No reviews yet.</div> : (dashboard.reviews || []).map((review) => (
                    <div className="owner-appointment appointment-card" key={review._id}>
                      <div className="appointment-card-head"><div><strong>{review.customer?.name || 'Customer'}</strong><p>{review.service}</p></div><span className={`badge badge-${review.status}`}>{review.status}</span></div>
                      <div className="review-stars">{'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}</div>
                      {review.title && <h3>{review.title}</h3>}<p>{review.comment}</p>
                      {review.response?.body && <div className="business-review-response"><strong>Your response</strong><p>{review.response.body}</p></div>}
                      <textarea placeholder="Write a public response" value={reviewResponseDrafts[review._id] ?? review.response?.body ?? ''} onChange={(e) => setReviewResponseDrafts({ ...reviewResponseDrafts, [review._id]: e.target.value })} maxLength="2000" />
                      <div className="owner-actions"><button type="button" className="button primary" disabled={busy || !canManageBusiness} onClick={() => respondToReview(review._id)}>Save response</button><button type="button" className="button ghost" disabled={busy || !canManageBusiness} onClick={() => reportReview(review._id)}>Report review</button></div>
                    </div>
                  ))}
                </div>
              </section>
            </>
          ) : <div className="panel"><p className="muted-text">Select a business to manage it here.</p></div>}
        </div>

              {dashboard && (
          <section className="owner-lower-grid">
                <div className="owner-lower-stack">
                  <section className="panel owner-form">
                    <button type="button" className="section-toggle" onClick={() => setTeamToolsOpen((open) => !open)} aria-expanded={teamToolsOpen}>
                      <span><span className="eyebrow">Team invites</span><strong>Invite team</strong></span>
                      <span>{teamToolsOpen ? 'Hide' : 'Show'}</span>
                    </button>
                    {teamToolsOpen && <>
                    {!canManageMembers && <p className="muted-text permission-note">Managers can view the team, but only owners can manage invitations and member permissions.</p>}
                    <form className="owner-invite-form" onSubmit={submitInvite}>
                      <input disabled={!canManageMembers} placeholder="Email address" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} required />
                      <select disabled={!canManageMembers} value={inviteForm.role} onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}>
                        <option value="staff">Staff</option>
                        <option value="manager">Manager</option>
                        <option value="owner">Owner</option>
                      </select>
                      <button type="submit" disabled={busy || !canManageMembers}>Create Invite Link</button>
                    </form>
                    {inviteLink && <p className="success-message">Invite link: <a href={inviteLink} target="_blank" rel="noreferrer">{inviteLink}</a> <button type="button" className="ghost" onClick={() => copyInviteLink(inviteLink)}>Copy link</button></p>}
                    <div className="owner-list">{dashboard.invites.map((invite) => <div key={invite._id} className="owner-list-item"><strong>{invite.email}</strong><span>{invite.role} · {invite.active ? 'pending' : 'used'} · expires {new Date(invite.expiresAt).toLocaleDateString()}</span>{invite.active && invite.inviteUrl && canManageMembers && <div className="owner-invite-link-actions"><a href={invite.inviteUrl} target="_blank" rel="noreferrer">{invite.inviteUrl}</a><button type="button" className="ghost" onClick={() => copyInviteLink(invite.inviteUrl)}>Copy invite link</button></div>}</div>)}</div>
                    </>}
                  </section>

                  <section className="panel owner-form owner-quick-card">
                    <button type="button" className="section-toggle" onClick={() => setQuickActionsOpen((open) => !open)} aria-expanded={quickActionsOpen}>
                      <span><span className="eyebrow">Quick actions</span><strong>Booking snapshot</strong></span>
                      <span>{quickActionsOpen ? 'Hide' : 'Show'}</span>
                    </button>
                    {quickActionsOpen && <>
                    <div className="owner-quick-actions">
                      <button type="button" className="ghost" onClick={() => scrollToSection('owner-services')}>Services</button>
                      <button type="button" className="ghost" onClick={() => scrollToSection('owner-staff')}>Staff</button>
                      <button type="button" className="ghost" onClick={() => scrollToSection('owner-appointments')}>Appointments</button>
                    </div>
                    <div className="owner-snapshot">
                      <div><span>Requested</span><strong>{appointmentStatusCounts.requested}</strong></div>
                      <div><span>Pending</span><strong>{appointmentStatusCounts.pending}</strong></div>
                      <div><span>Confirmed</span><strong>{appointmentStatusCounts.confirmed}</strong></div>
                      <div><span>Completed</span><strong>{appointmentStatusCounts.completed}</strong></div>
                    </div>
                    </>}
                  </section>
                </div>

                <section className="panel">
                  <p className="eyebrow">Team members</p>
                  <h2>Permissions</h2>
                  <div className="owner-list">
                    {dashboard.members.map((member) => (
                      <div key={member._id} className="owner-list-item owner-member-row">
                        <div>
                          <strong>{member.user?.name || 'Unlinked member'}</strong>
                          <div>{member.user?.email || 'No email'}</div>
                          <span className={`badge badge-role-${member.role}`}>{member.role}{member.active ? '' : ' · disabled'}</span>
                        </div>
                        <div className="owner-actions">
                          <select disabled={!canManageMembers} value={member.role} onChange={(e) => updateMember(member._id, { role: e.target.value })}>
                            {['owner', 'manager', 'staff'].map((role) => <option key={role} value={role}>{role}</option>)}
                          </select>
                          <select disabled={!canManageMembers} value={String(member.active)} onChange={(e) => updateMember(member._id, { active: e.target.value === 'true' })}>
                            <option value="true">active</option>
                            <option value="false">disabled</option>
                          </select>
                          <button type="button" className="ghost" disabled={!canManageMembers} onClick={() => deleteMember(member._id)}>Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              </section>
        )}
      </section>
    </div>
  );
}

export default OwnerDashboard;
